import { useState } from 'react';
import DatePicker from '../DatePicker';
import { parseDate } from '../../utils/dateUtils';
import { useTranslation } from '../../i18n';
import { 
  SIDEBAR_MODE, 
  MILESTONE_ICON, 
  MILESTONE_ICON_UNICODE,
  DATE_PICKER_FIELD 
} from '../../constants/types';
import { TitleField, DescriptionField, ProjectField, FormActions } from './shared/FormFields';
import useFormState from './shared/useFormState';
import './MilestoneForm.scss';

/**
 * Milestone form component for creating and editing milestones.
 * Handles milestone-specific fields: date, icon, and date lock.
 * 
 * @param {Object} props
 * @param {string} props.mode - 'create' or 'edit'
 * @param {Object} props.data - Milestone data for edit mode
 * @param {Array} props.projects - Available projects
 * @param {string} props.selectedProjectId - Pre-selected project for new items
 * @param {Function} props.onSave - Save handler (formData) => void
 * @param {Function} props.onDelete - Delete handler (id) => void
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onChange - Live preview change handler
 */
function MilestoneForm({ 
  mode, 
  data, 
  projects,
  selectedProjectId,
  onSave, 
  onDelete, 
  onClose,
  onChange
}) {
  const { t, formatDate } = useTranslation();
  
  // Generate icon options with translated labels
  const milestoneIconOptions = [
    { value: MILESTONE_ICON.FLAG, label: t('milestone.icons.flag'), icon: MILESTONE_ICON_UNICODE[MILESTONE_ICON.FLAG] },
    { value: MILESTONE_ICON.DIAMOND, label: t('milestone.icons.diamond'), icon: MILESTONE_ICON_UNICODE[MILESTONE_ICON.DIAMOND] },
    { value: MILESTONE_ICON.STAR, label: t('milestone.icons.star'), icon: MILESTONE_ICON_UNICODE[MILESTONE_ICON.STAR] },
    { value: MILESTONE_ICON.CIRCLE, label: t('milestone.icons.circle'), icon: MILESTONE_ICON_UNICODE[MILESTONE_ICON.CIRCLE] }
  ];

  // Use shared form state for common fields
  const { 
    title, 
    description, 
    projectId, 
    handleTitleChange, 
    handleDescriptionChange, 
    handleProjectChange,
    notifyChange,
    inputRef 
  } = useFormState({ mode, data, selectedProjectId, projects, onChange });

  // Milestone-specific state
  const initialIcon = mode === SIDEBAR_MODE.EDIT && data ? (data.icon || MILESTONE_ICON.FLAG) : MILESTONE_ICON.FLAG;
  const initialDate = data?.date ? parseDate(data.date) : new Date();

  const [icon, setIcon] = useState(initialIcon);
  const [date, setDate] = useState(initialDate);
  const [locked, setLocked] = useState(Boolean(data?.locked));
  
  // Date picker visibility
  const [activePicker, setActivePicker] = useState(null);

  const handleIconChange = (newIcon) => {
    setIcon(newIcon);
    notifyChange({ icon: newIcon });
  };

  const handleDateChange = (newDate) => {
    if (locked) return;
    setDate(newDate);
    notifyChange({ date: newDate });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!projectId) return;

    onSave({
      title: title.trim(),
      description: description.trim(),
      projectId,
      icon,
      date,
      locked
    });
  };

  const handleDeleteClick = () => {
    onDelete(data.id);
  };

  const isEditing = mode === SIDEBAR_MODE.EDIT;
  const headerTitle = isEditing ? t('milestone.edit') : t('milestone.new');
  
  // Check if milestone is read-only (from shared project with read permission)
  const selectedProject = projects.find(p => p.id === projectId);
  const isReadOnly = selectedProject?.permission === 'read';
  const ownerInfo = isReadOnly && selectedProject?.sharedBy 
    ? `${t('sharing.sharedBy')} ${selectedProject.sharedBy}` 
    : null;

  return (
    <>
      <div className="milestone-form__header">
        <h3 className="milestone-form__title">{headerTitle}</h3>
        <button 
          className="milestone-form__close" 
          onClick={onClose}
          title={t('common.close')}
        >
          {'\u00D7'}
        </button>
      </div>

      {/* Read-only notice for shared items */}
      {isReadOnly && (
        <div className="milestone-form__readonly-notice">
          <span className="milestone-form__readonly-icon">ℹ️</span>
          <div className="milestone-form__readonly-text">
            <div>{t('sharing.readOnlyInfo')}</div>
            {ownerInfo && <div className="milestone-form__readonly-owner">{ownerInfo}</div>}
          </div>
        </div>
      )}

      {/* Date field */}
      <div className="milestone-form__dates-section">
        <div className="milestone-form__date-field">
          <div className="milestone-form__date-with-lock">
            <button
              type="button"
              className={`milestone-form__date-btn ${locked ? 'milestone-form__date-btn--locked' : ''}`}
              onClick={() => !locked && !isReadOnly && setActivePicker(activePicker === DATE_PICKER_FIELD.DATE ? null : DATE_PICKER_FIELD.DATE)}
              disabled={locked || isReadOnly}
            >
              {formatDate(date)}
            </button>
            <button
              type="button"
              className={`milestone-form__lock-btn ${locked ? 'milestone-form__lock-btn--locked' : ''}`}
              onClick={() => !isReadOnly && setLocked(!locked)}
              title={locked ? t('milestone.unlockDate') : t('milestone.lockDate')}
              disabled={isReadOnly}
            >
              🔒
            </button>
          </div>
          {activePicker === DATE_PICKER_FIELD.DATE && !locked && !isReadOnly && (
            <DatePicker
              value={date}
              onChange={handleDateChange}
              onClose={() => setActivePicker(null)}
            />
          )}
        </div>
      </div>

      <form className="milestone-form__form" onSubmit={handleSubmit}>
        <TitleField
          value={title}
          onChange={handleTitleChange}
          placeholder={t('milestone.placeholder')}
          inputRef={inputRef}
          disabled={isReadOnly}
        />

        <DescriptionField
          value={description}
          onChange={handleDescriptionChange}
          disabled={isReadOnly}
        />

        <div className="form-field">
          <label className="form-field__label">{t('milestone.icon')}</label>
          <div className="milestone-form__icons">
            {milestoneIconOptions.map(iconOption => (
              <button
                key={iconOption.value}
                type="button"
                className={`milestone-form__icon-btn ${icon === iconOption.value ? 'milestone-form__icon-btn--active' : ''}`}
                onClick={() => !isReadOnly && handleIconChange(iconOption.value)}
                title={iconOption.label}
                disabled={isReadOnly}
              >
                {iconOption.icon}
              </button>
            ))}
          </div>
        </div>

        <ProjectField
          value={projectId}
          onChange={handleProjectChange}
          projects={projects}
          disabled={isReadOnly}
        />

        <FormActions
          mode={mode}
          entityType="milestone"
          entityTitle={data?.title}
          onDelete={handleDeleteClick}
          onClose={onClose}
          onSubmit={handleSubmit}
          canSubmit={title.trim() && projectId}
          disabled={isReadOnly}
        />
      </form>
    </>
  );
}

export default MilestoneForm;
